// @ts-nocheck - MUI 7.x Grid item prop type compatibility
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Grid,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { fleetAdminAPI } from '../services/api';

interface FleetClientOption {
  customer_id: string;
  razon_social: string;
}

interface AlertRule {
  alert_type: string;
  threshold: number;
  recipients: string[];
  enabled: boolean;
}

const ALERT_TYPE_LABELS: Record<string, string> = {
  low_toner: 'Toner Bajo',
  device_offline: 'Dispositivo Offline',
  quota_exceeded: 'Cuota Excedida',
  high_volume: 'Alto Volumen',
};

const ALERT_TYPE_COLORS: Record<string, 'warning' | 'error' | 'info' | 'default'> = {
  low_toner: 'warning',
  device_offline: 'error',
  quota_exceeded: 'info',
  high_volume: 'default',
};

const THRESHOLD_LABELS: Record<string, string> = {
  low_toner: 'Umbral (%)',
  device_offline: 'Minutos offline',
  quota_exceeded: 'Uso (%)',
  high_volume: 'Páginas',
};

const FleetAlertRules: React.FC = () => {
  const [clients, setClients] = useState<FleetClientOption[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [endpointUnavailable, setEndpointUnavailable] = useState(false);

  // New rule form state
  const [newAlertType, setNewAlertType] = useState('low_toner');
  const [newThreshold, setNewThreshold] = useState<number>(10);
  const [newRecipients, setNewRecipients] = useState('');
  const [newEnabled, setNewEnabled] = useState(true);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  const loadClients = async () => {
    setClientsLoading(true);
    try {
      const data = await fleetAdminAPI.getFleetClients();
      setClients(data);
    } catch {
      setEndpointUnavailable(true);
    } finally {
      setClientsLoading(false);
    }
  };

  const loadRules = async (clientId: string) => {
    setRulesLoading(true);
    try {
      const data = await fleetAdminAPI.getAlertRules(clientId);
      setRules(data || []);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404 || status === 501) {
        setEndpointUnavailable(true);
        setRules([]);
      } else {
        setSnackbar({ open: true, message: 'Error al cargar reglas de alerta.', severity: 'error' });
        setRules([]);
      }
    } finally {
      setRulesLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      loadRules(selectedClientId);
    } else {
      setRules([]);
    }
  }, [selectedClientId]);

  const handleAddRule = () => {
    if (!newRecipients.trim()) {
      setSnackbar({ open: true, message: 'Ingrese al menos un destinatario.', severity: 'error' });
      return;
    }
    const recipients = newRecipients
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.length > 0);

    setRules([
      ...rules,
      {
        alert_type: newAlertType,
        threshold: newThreshold,
        recipients,
        enabled: newEnabled,
      },
    ]);
    // Reset form
    setNewRecipients('');
    setNewThreshold(10);
    setNewEnabled(true);
  };

  const handleDeleteRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const handleToggleRule = (index: number) => {
    setRules(
      rules.map((r, i) => (i === index ? { ...r, enabled: !r.enabled } : r))
    );
  };

  const handleSave = async () => {
    if (!selectedClientId) {
      setSnackbar({ open: true, message: 'Seleccione un cliente.', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      await fleetAdminAPI.setAlertRules(selectedClientId, rules);
      setSnackbar({ open: true, message: 'Reglas guardadas exitosamente.', severity: 'success' });
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404 || status === 501) {
        setSnackbar({
          open: true,
          message: 'El endpoint de alertas no está disponible todavía.',
          severity: 'info',
        });
      } else {
        setSnackbar({ open: true, message: 'Error al guardar reglas.', severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Fleet - Reglas de Alerta
        </Typography>
      </Box>

      {endpointUnavailable && (
        <Alert severity="info" sx={{ mb: 3 }}>
          El endpoint de alertas fleet aún no está disponible — se implementará en una próxima actualización.
        </Alert>
      )}

      {/* Client selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel id="client-alert-select-label">Seleccionar Cliente</InputLabel>
          <Select
            labelId="client-alert-select-label"
            value={selectedClientId}
            label="Seleccionar Cliente"
            onChange={(e) => setSelectedClientId(e.target.value)}
            disabled={clientsLoading}
          >
            {clientsLoading ? (
              <MenuItem value="" disabled>Cargando clientes...</MenuItem>
            ) : clients.length === 0 ? (
              <MenuItem value="" disabled>No hay clientes disponibles</MenuItem>
            ) : (
              clients.map((c) => (
                <MenuItem key={c.customer_id} value={c.customer_id}>
                  {c.razon_social || c.customer_id} ({c.customer_id})
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Paper>

      {/* Rules table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Reglas Configuradas
          </Typography>
          {selectedClientId && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !selectedClientId}
            >
              Guardar Reglas
            </Button>
          )}
        </Box>
        <Divider />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tipo de Alerta</TableCell>
                <TableCell align="right">Umbral</TableCell>
                <TableCell>Destinatarios</TableCell>
                <TableCell align="center">Activa</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rulesLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    Cargando reglas...
                  </TableCell>
                </TableRow>
              ) : !selectedClientId ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    Seleccione un cliente para ver sus reglas de alerta.
                  </TableCell>
                </TableRow>
              ) : rules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No hay reglas configuradas. Agregue una regla a continuación.
                  </TableCell>
                </TableRow>
              ) : (
                rules.map((rule, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip
                        label={ALERT_TYPE_LABELS[rule.alert_type] || rule.alert_type}
                        color={ALERT_TYPE_COLORS[rule.alert_type] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{rule.threshold}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {rule.recipients.map((r, i) => (
                          <Chip key={i} label={r} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={rule.enabled}
                        onChange={() => handleToggleRule(index)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Eliminar regla">
                        <IconButton size="small" color="error" onClick={() => handleDeleteRule(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add rule form */}
      {selectedClientId && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Agregar Nueva Regla
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} alignItems="center">
            <Grid xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Alerta</InputLabel>
                <Select
                  value={newAlertType}
                  label="Tipo de Alerta"
                  onChange={(e) => setNewAlertType(e.target.value)}
                >
                  <MenuItem value="low_toner">Toner Bajo</MenuItem>
                  <MenuItem value="device_offline">Dispositivo Offline</MenuItem>
                  <MenuItem value="quota_exceeded">Cuota Excedida</MenuItem>
                  <MenuItem value="high_volume">Alto Volumen</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label={THRESHOLD_LABELS[newAlertType] || 'Umbral'}
                type="number"
                value={newThreshold}
                onChange={(e) => setNewThreshold(Number(e.target.value))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Destinatarios (separados por coma)"
                value={newRecipients}
                onChange={(e) => setNewRecipients(e.target.value)}
                placeholder="admin@empresa.com, it@empresa.com"
              />
            </Grid>
            <Grid xs={12} sm={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newEnabled}
                    onChange={(e) => setNewEnabled(e.target.checked)}
                  />
                }
                label="Activa"
              />
            </Grid>
            <Grid xs={12} sm={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddRule}
                fullWidth
              >
                Agregar
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FleetAlertRules;
